import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, CheckCircle, Zap } from 'lucide-react';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function AdminApify() {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const [tokens, setTokens] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/apify/status`, { headers });
      setTokens(res.data.tokens);
      setActiveIndex(res.data.activeIndex);
    } catch {
      alert('Erro ao carregar status Apify');
    } finally {
      setLoading(false);
    }
  };

  const switchToken = async (index) => {
    setSwitching(index);
    try {
      await axios.post(`${API_URL}/admin/apify/switch`, { index }, { headers });
      setActiveIndex(index);
      setTokens(prev => prev.map((t, i) => ({ ...t, active: i === index })));
    } catch {
      alert('Erro ao trocar conta');
    } finally {
      setSwitching(null);
    }
  };

  useEffect(() => {
    if (!token) { navigate('/backoffice/login'); return; }
    fetchStatus();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader activePage="apify" />

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Gestão Apify</h2>
            <p className="text-sm text-gray-500">Contas e saldo disponível</p>
          </div>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
        {loading ? (
          <div className="text-center py-16 text-gray-400">A carregar...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tokens.map((t) => {
              const pct = t.usedUsd && t.limitUsd ? Math.min(100, Math.round((parseFloat(t.usedUsd) / parseFloat(t.limitUsd)) * 100)) : 0;
              const isActive = t.index === activeIndex;
              const remaining = t.usedUsd && t.limitUsd ? (parseFloat(t.limitUsd) - parseFloat(t.usedUsd)).toFixed(2) : null;

              return (
                <div key={t.index} className={`bg-white rounded-2xl p-6 shadow-sm border-2 transition ${isActive ? 'border-purple-500' : 'border-gray-100'}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-bold text-gray-900 text-lg">{t.label}</p>
                      {t.email && <p className="text-xs text-gray-400 mt-0.5">{t.email}</p>}
                    </div>
                    {isActive && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                        <Zap className="w-3 h-3" /> Ativa
                      </span>
                    )}
                  </div>

                  {t.error ? (
                    <p className="text-sm text-red-500">Erro ao carregar dados</p>
                  ) : (
                    <>
                      <div className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500">Usado</span>
                          <span className="font-semibold text-gray-900">${t.usedUsd} / ${t.limitUsd}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full transition-all ${pct > 80 ? 'bg-red-500' : pct > 60 ? 'bg-orange-400' : 'bg-purple-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{pct}% usado • ${remaining} restantes</p>
                      </div>
                    </>
                  )}

                  {!isActive && (
                    <button
                      onClick={() => switchToken(t.index)}
                      disabled={switching === t.index}
                      className="w-full mt-4 flex items-center justify-center gap-2 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition text-sm font-medium"
                    >
                      {switching === t.index ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Ativar esta conta
                    </button>
                  )}
                  {isActive && (
                    <div className="w-full mt-4 py-2 bg-purple-50 text-purple-700 rounded-lg text-center text-sm font-medium">
                      Conta em uso
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-gray-400 text-center mt-8">
          Para tornar a conta ativa permanente após deploy, define <code className="bg-gray-100 px-1 rounded">APIFY_ACTIVE_TOKEN_INDEX=0</code> ou <code className="bg-gray-100 px-1 rounded">1</code> no .env da VPS.
        </p>
      </div>
    </div>
  );
}
